import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, CardContent, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, TextField, Alert, CircularProgress,
  Divider, LinearProgress, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import {
  AccountBalanceWallet, TrendingUp, CalendarToday, Today,
  CheckCircle, Cancel, HelpOutline, Refresh
} from '@mui/icons-material';
import {
  fetchEarningsSummary, fetchEarningsHistory, fetchPayoutEligibility,
  fetchPayoutPreview, submitPAN, clearPayoutPreview, clearPanSuccess
} from '../../store/slices/earningsSlice';
import { fetchBankAccounts, requestWithdrawal, fetchWithdrawals, fetchPayoutUPIMethods } from '../../store/slices/walletSlice';
import BankAccountForm from '../../components/wallet/BankAccountForm';
import BankAccountList from '../../components/wallet/BankAccountList';

const fmt = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ title, value, icon, color = 'primary.main' }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
      </Box>
      <Typography variant="h5" fontWeight="bold">{value}</Typography>
    </CardContent>
  </Card>
);

const CheckItem = ({ label, passed, action, onAction }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
    {passed
      ? <CheckCircle color="success" sx={{ mr: 1 }} />
      : <Cancel color="error" sx={{ mr: 1 }} />}
    <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
    {!passed && action && (
      <Button size="small" variant="outlined" onClick={onAction}>{action}</Button>
    )}
  </Box>
);

export default function EarningsDashboard() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const [tab, setTab] = useState(isNaN(initialTab) ? 0 : Math.min(initialTab, 3));
  const [panInput, setPanInput] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState('');

  const { summary, history, eligibility, payoutPreview, loading, error, panSuccess } = useSelector(s => s.earnings);
  const { bankAccounts, payoutUPIMethods, withdrawals, loading: walletLoading } = useSelector(s => s.wallet);

  useEffect(() => {
    dispatch(fetchEarningsSummary());
    dispatch(fetchEarningsHistory());
    dispatch(fetchPayoutEligibility());
    dispatch(fetchBankAccounts());
    dispatch(fetchWithdrawals());
    dispatch(fetchPayoutUPIMethods());
  }, [dispatch]);

  useEffect(() => {
    if (panSuccess) {
      setTimeout(() => dispatch(clearPanSuccess()), 3000);
    }
  }, [panSuccess, dispatch]);

  const handlePreview = () => {
    if (!withdrawAmount || isNaN(withdrawAmount) || Number(withdrawAmount) <= 0) return;
    dispatch(fetchPayoutPreview(withdrawAmount));
  };

  const handleWithdraw = async () => {
    setWithdrawError('');
    setWithdrawSuccess('');
    if (!withdrawAmount || !selectedPayoutMethod) {
      setWithdrawError('Enter amount and select a payout method');
      return;
    }

    // Parse method selection: "bank:5" or "upi:42"
    const [type, idStr] = selectedPayoutMethod.split(':');
    const methodId = Number(idStr);
    const bankAccountId = type === 'bank' ? methodId : 0;
    const paymentMethodId = type === 'upi' ? methodId : 0;

    // NOTE: send raw rupees, not paise -- the backend handler already does its
    // own wallet.RupeesToPaise(req.Amount) conversion (same as every other
    // amount-taking endpoint: refund, recharge, payout preview). Pre-converting
    // here double-converts (e.g. a ₹10 request becomes a ₹1000 check/payout).
    const key = `withdraw-${Date.now()}`;
    const result = await dispatch(requestWithdrawal({
      amount: Number(withdrawAmount),
      bankAccountId,
      paymentMethodId,
      idempotencyKey: key,
    }));
    if (requestWithdrawal.fulfilled.match(result)) {
      setWithdrawSuccess('Payout request submitted! Admin will process it shortly.');
      setWithdrawAmount('');
      setSelectedPayoutMethod('');
      dispatch(clearPayoutPreview());
      dispatch(fetchEarningsSummary());
      dispatch(fetchPayoutEligibility());
      dispatch(fetchWithdrawals());
    } else {
      setWithdrawError(result.payload || 'Failed to submit payout request');
    }
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'success';
    if (status === 'rejected') return 'error';
    if (status === 'processing') return 'warning';
    return 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">My Earnings</Typography>
        <IconButton onClick={() => { dispatch(fetchEarningsSummary()); dispatch(fetchPayoutEligibility()); }}>
          <Refresh />
        </IconButton>
      </Box>

      {/* Stats Row */}
      {loading.summary ? <LinearProgress sx={{ mb: 2 }} /> : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Available Balance" value={fmt(summary?.available_balance)} icon={<AccountBalanceWallet />} color="success.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Earned" value={fmt(summary?.total_earned)} icon={<TrendingUp />} color="primary.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="This Month" value={fmt(summary?.this_month)} icon={<CalendarToday />} color="info.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Today" value={fmt(summary?.today)} icon={<Today />} color="warning.main" />
          </Grid>
        </Grid>
      )}

      {summary?.pending_payout > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Payout of {fmt(summary.pending_payout)} is pending approval.
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Earnings History" />
        <Tab label="Request Payout" />
        <Tab label="Payout History" />
        <Tab label="PAN & Payout Methods" />
      </Tabs>

      {/* Tab 0: Earnings History */}
      {tab === 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount Earned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading.history && (
                <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading.history && history.transactions.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No earnings yet. Complete consultations to earn.</TableCell></TableRow>
              )}
              {history.transactions.map(txn => (
                <TableRow key={txn.id}>
                  <TableCell>{new Date(txn.created_at).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell>{txn.description || 'Consultation earning'}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>{fmt(txn.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {history.has_more && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button onClick={() => dispatch(fetchEarningsHistory({ cursor: history.next_cursor }))}>
                Load More
              </Button>
            </Box>
          )}
        </TableContainer>
      )}

      {/* Tab 1: Request Payout */}
      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Eligibility Checklist</Typography>
                {loading.eligibility ? <CircularProgress size={20} /> : eligibility ? (
                  <>
                    <CheckItem label="PAN verified" passed={eligibility.checks?.pan_verified} action="Add PAN" onAction={() => setTab(3)} />
                    <CheckItem label="Payout method added" passed={eligibility.checks?.payout_method_added} action="Add Method" onAction={() => setTab(3)} />
                    <CheckItem
                      label={`Min balance ₹200 (available: ${fmt(eligibility.available_balance)})`}
                      passed={eligibility.checks?.min_balance_met}
                    />
                    <CheckItem label="No pending payout" passed={eligibility.checks?.no_pending_payout} />
                    <Divider sx={{ my: 1 }} />
                    <Chip
                      label={eligibility.eligible ? 'Eligible for payout' : 'Not eligible yet'}
                      color={eligibility.eligible ? 'success' : 'default'}
                      size="small"
                    />
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Request Payout</Typography>
                {withdrawSuccess && <Alert severity="success" sx={{ mb: 2 }}>{withdrawSuccess}</Alert>}
                {withdrawError && <Alert severity="error" sx={{ mb: 2 }}>{withdrawError}</Alert>}

                <TextField
                  fullWidth label="Amount (₹)" type="number" value={withdrawAmount}
                  onChange={e => { setWithdrawAmount(e.target.value); dispatch(clearPayoutPreview()); }}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  sx={{ mb: 2 }}
                  disabled={!eligibility?.eligible}
                />

                <TextField
                  select fullWidth label="Payout Method" value={selectedPayoutMethod}
                  onChange={e => setSelectedPayoutMethod(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                  disabled={!eligibility?.eligible}
                >
                  <option value="">Select payout method</option>
                  {(bankAccounts || []).filter(b => b.provider_registered).map(b => (
                    <option key={`bank-${b.id}`} value={`bank:${b.id}`}>
                      Bank: {b.bank_name} — {b.account_number_masked || b.account_number}
                    </option>
                  ))}
                  {(payoutUPIMethods || []).filter(u => u.provider_registered).map(u => (
                    <option key={`upi-${u.id}`} value={`upi:${u.id}`}>
                      UPI: {u.upi_id}
                    </option>
                  ))}
                </TextField>

                <Button
                  variant="outlined" fullWidth onClick={handlePreview}
                  disabled={!withdrawAmount || !eligibility?.eligible}
                  sx={{ mb: 2 }}
                >
                  Preview TDS
                </Button>

                {loading.preview && <LinearProgress sx={{ mb: 2 }} />}

                {payoutPreview && (
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Payout Breakdown</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Gross Amount</Typography>
                      <Typography variant="body2">{fmt(payoutPreview.gross_amount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="warning.main">
                        TDS ({payoutPreview.tds_rate}%) <Tooltip title={payoutPreview.note}><HelpOutline fontSize="inherit" /></Tooltip>
                      </Typography>
                      <Typography variant="body2" color="warning.main">- {fmt(payoutPreview.tds_amount)}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="bold">Net Transfer</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">{fmt(payoutPreview.net_amount)}</Typography>
                    </Box>
                    {payoutPreview.tds_threshold_remaining > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        TDS-free threshold remaining: {fmt(payoutPreview.tds_threshold_remaining)}
                      </Typography>
                    )}
                  </Box>
                )}

                <Button
                  variant="contained" fullWidth color="primary"
                  onClick={handleWithdraw}
                  disabled={!eligibility?.eligible || !withdrawAmount || !selectedPayoutMethod || walletLoading.withdrawal}
                >
                  {walletLoading.withdrawal ? 'Submitting...' : 'Confirm & Request Payout'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Payout History */}
      {tab === 2 && (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(withdrawals || []).length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>No payout requests yet.</TableCell></TableRow>
              )}
              {(withdrawals || []).map(w => (
                <TableRow key={w.id}>
                  <TableCell>{new Date(w.created_at).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell>{fmt(w.amount / 100)}</TableCell>
                  <TableCell>{w.bank_name || '—'}</TableCell>
                  <TableCell><Chip label={w.status} color={statusColor(w.status)} size="small" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 3: PAN & Payout Methods */}
      {tab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>PAN Card</Typography>
                {eligibility?.checks?.pan_verified ? (
                  <Alert severity="success" icon={<CheckCircle />}>PAN verified</Alert>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Required for payouts above ₹50,000/year (Section 194J TDS).
                    </Typography>
                    {panSuccess && <Alert severity="success" sx={{ mb: 2 }}>PAN saved successfully!</Alert>}
                    {error.pan && <Alert severity="error" sx={{ mb: 2 }}>{error.pan}</Alert>}
                    <TextField
                      fullWidth label="PAN Number" value={panInput}
                      onChange={e => setPanInput(e.target.value.toUpperCase())}
                      placeholder="ABCDE1234F"
                      inputProps={{ maxLength: 10 }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained" fullWidth
                      onClick={() => dispatch(submitPAN(panInput))}
                      disabled={loading.pan || panInput.length !== 10}
                    >
                      {loading.pan ? 'Saving...' : 'Save PAN'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Payout Methods</Typography>
                <BankAccountForm />
                <Box sx={{ mt: 2 }}>
                  <BankAccountList />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
